import { useState, useEffect } from 'react'
import { errorToastManager } from 'utility/errorToastManager'

interface NetworkStatus {
  isOnline: boolean
  isSlowConnection: boolean
  connectionType: string
}

/**
 * Custom hook to monitor network status and handle connectivity changes
 */
export const useNetworkStatus = () => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    isSlowConnection: false,
    connectionType: 'unknown',
  })

  const [wasOffline, setWasOffline] = useState(false)

  useEffect(() => {
    const updateNetworkStatus = () => {
      const isOnline = navigator.onLine
      let connectionType = 'unknown'
      let isSlowConnection = false

      // Get connection info if available (experimental API)
      if ('connection' in navigator) {
        const { connection } = navigator as any
        connectionType = connection?.effectiveType || 'unknown'
        isSlowConnection =
          connection?.effectiveType === 'slow-2g' ||
          connection?.effectiveType === '2g'
      }

      setNetworkStatus({
        isOnline,
        isSlowConnection,
        connectionType,
      })

      // Handle network recovery
      if (isOnline && wasOffline) {
        console.log('Network recovered')
        errorToastManager.handleNetworkRecovery().catch((error) => {
          console.error('Error handling network recovery:', error)
        })
        setWasOffline(false)
      }

      // Track when we go offline
      if (!isOnline && !wasOffline) {
        setWasOffline(true)
      }
    }

    const handleOnline = () => {
      console.log('Network: Online event detected')
      updateNetworkStatus()
    }

    const handleOffline = () => {
      console.log('Network: Offline event detected')
      updateNetworkStatus()
    }

    // Initial check
    updateNetworkStatus()

    // Add event listeners
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Listen for connection changes (if supported)
    if ('connection' in navigator) {
      const { connection } = navigator as any
      connection?.addEventListener?.('change', updateNetworkStatus)
    }

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)

      if ('connection' in navigator) {
        const { connection } = navigator as any
        connection?.removeEventListener?.('change', updateNetworkStatus)
      }
    }
  }, [wasOffline])

  return {
    ...networkStatus,
    hasNetworkError: errorToastManager.isNetworkErrorActive(),
  }
}
